import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CountriesDropdown } from "../components/dropdowns/CountriesDropdown";
import { CurrencyLogoDropdown } from "../components/dropdowns/CurrencyLogoDropdown";
import DateOfBirthPicker from "../components/inputs/DateOfBirthPicker";
import GlobalTextInputField from "../components/inputs/GlobalTextInputField";
import AccessInfoBadges from "../components/layout/AccessInfoBadges";
import ExchangeInfoPanel from "../components/layout/ExchangeInfoPanel";
import { commonApiServices } from "../services/api/Common";
import { primeFiatApiServices } from "../services/api/PrimeFiat";
import { GetCountriesListData } from "../services/models/GetCountriesList";
import { GetTokenListData } from "../services/models/GetTokenList";
import { CALLBACK_URL, REDIRECT_URL } from "../utils/globals";
import { isValidDobDate } from "../utils/dob";

const FIAT_CURRENCIES = [
  {
    id: "CAD",
    currency: "CAD",
    currencyLogo: "https://assets.tylt.money/country-images/CAN.png",
  },
  {
    id: "EUR",
    currency: "EUR",
    currencyLogo: "https://assets.tylt.money/holdings-icons/EUR.png",
  },
];

const PROVINCE_CODES = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

const PROVINCE_OPTIONS = Object.entries(PROVINCE_CODES).map(([code, name]) => ({
  id: code,
  country: name,
}));

const dropdownProps = {
  color: "bg-page-body",
  baseColor: "bg-page-body",
  borderColor: "border-border-subtle",
  expandedBorderColor: "border-border-subtle",
  titleColor: "text-text-subtle",
};

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="text-[#1418A8] text-[12px] font-bold mb-[10px]">
      {children}
    </h2>
  );
}

function HomePage() {
  const navigate = useNavigate();

  useEffect(() => {
    const savedInstanceId = sessionStorage.getItem("obd_instance_id");
    const savedRegion = sessionStorage.getItem("obd_region") || "eur";
    if (savedInstanceId) {
      navigate(`/review/${savedRegion}/${savedInstanceId}`, { replace: true });
    }
  }, [navigate]);

  const [countries, setCountries] = useState<GetCountriesListData[]>([]);
  const [tokens, setTokens] = useState<GetTokenListData[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  const [fiatCurrency, setFiatCurrency] = useState<string | undefined>();
  const [assetId, setAssetId] = useState<string | number | undefined>();
  const [amount, setAmount] = useState("");
  const [networkId, setNetworkId] = useState<string | number | undefined>();
  const [walletAddress, setWalletAddress] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [residenceCountryId, setResidenceCountryId] = useState<
    string | number | undefined
  >();
  const [birthCountryId, setBirthCountryId] = useState<
    string | number | undefined
  >();
  const [mobile, setMobile] = useState("");
  const [province, setProvince] = useState<string | number | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoadingLists(true);
      try {
        const [countriesRes, tokensRes] = await Promise.all([
          commonApiServices.getCountriesList({ isEuropeFlow: true }),
          commonApiServices.getTokenList().catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setCountries(countriesRes.data || []);
        setTokens(tokensRes.data?.length ? tokensRes.data : []);
      } catch {
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedToken = useMemo(
    () => tokens.find((token) => token.currencyId === assetId),
    [tokens, assetId],
  );

  const selectedNetwork = useMemo(
    () =>
      selectedToken?.networks?.find(
        (network) => network.networkId === networkId,
      ),
    [selectedToken, networkId],
  );

  const isWalletAddressValid = useMemo(() => {
    if (!walletAddress.trim() || !selectedNetwork?.networkRegex) return true;
    try {
      return new RegExp(selectedNetwork.networkRegex).test(
        walletAddress.trim(),
      );
    } catch {
      return true;
    }
  }, [walletAddress, selectedNetwork]);

  const assetOptions = useMemo(
    () =>
      tokens.map((token) => ({
        id: token.currencyId,
        currency: token.currencySymbol,
        currencyLogo: token.currencyImg,
        currencyName: token.currencyName,
        isToken: true,
        isReceive: true,
      })),
    [tokens],
  );

  const networkOptions = useMemo(() => {
    const networks = selectedToken?.networks?.length
      ? selectedToken.networks
      : [];
    return networks.map((network) => ({
      id: network.networkId,
      currency: network.networkSymbol || network.networkName,
      currencyLogo: network.networkImg,
    }));
  }, [selectedToken]);

  useEffect(() => {
    setNetworkId(undefined);
    setWalletAddress("");
  }, [assetId]);

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (/^\d*\.?\d*$/.test(next)) {
      setAmount(next);
    }
  };

  const handleSubmit = async () => {
    setError("");

    const residenceCountry = countries.find(
      (country) => country.id === residenceCountryId,
    );
    const birthCountry = countries.find(
      (country) => country.id === birthCountryId,
    );

    const isCad = fiatCurrency === "CAD";

    if (
      !fiatCurrency ||
      !selectedToken ||
      !amount ||
      !selectedNetwork ||
      !walletAddress ||
      !firstName ||
      !lastName ||
      !email ||
      !dob ||
      !isValidDobDate(dob) ||
      !residenceCountry ||
      !birthCountry ||
      (isCad && (!mobile.trim() || !province))
    ) {
      setError(
        !dob || !isValidDobDate(dob)
          ? "Please enter a valid date of birth (18+)."
          : "Please fill in all fields.",
      );
      return;
    }

    if (!isWalletAddressValid) {
      setError(
        `Invalid wallet address for ${selectedNetwork.networkName || "the selected network"}.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const merchantOrderId = `obd.${Date.now()}`;
      const response = await primeFiatApiServices.createPayin(
        {
          merchantOrderId,
          callBackUrl: CALLBACK_URL,
          redirectUrl: REDIRECT_URL,
          amount: Number(amount),
          currencySymbol: String(fiatCurrency),
          cryptoCurrencySymbol: selectedToken.currencySymbol,
          cryptoUI: 1,
          paymentMethod:
            fiatCurrency === "EUR"
              ? "open_banking"
              : fiatCurrency === "CAD"
                ? "interac_e_transfer"
                : "",
          merchantDetails: {
            merchantName: "TYLT Exchange",
            merchantUrl: window.location.origin,
            merchantInternalId: `merchant.${Date.now()}`,
          },
          userDetails: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            country: residenceCountry.countryShort2,
            countryOfBirth: birthCountry.countryShort2,
            dob,
            ...(fiatCurrency === "CAD"
              ? {
                  mobile: mobile.trim(),
                  province:
                    PROVINCE_CODES[
                      String(province) as keyof typeof PROVINCE_CODES
                    ],
                }
              : {}),
          },
          walletDetails: {
            address: walletAddress.trim(),
            network: selectedNetwork.networkSymbol,
          },
          transferType: "external",
        },
        fiatCurrency,
      );

      const instanceId = response.data?.instanceId;
      if (!instanceId) {
        setError(response.msg || "Failed to create purchase instance.");
        return;
      }

      sessionStorage.setItem("obd_instance_id", instanceId);
      sessionStorage.setItem(
        "obd_region",
        fiatCurrency === "EUR" ? "eur" : "cad",
      );
      navigate(
        `/review/${fiatCurrency === "EUR" ? "eur" : "cad"}/${instanceId}`,
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.msg ||
          err?.message ||
          "Failed to create purchase. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative h-dvh w-full overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <div className="noise w-full h-full" />
      </div>

      <div className="relative h-full w-full flex items-stretch justify-center px-[16px] lg:px-[80px] py-[20px] lg:py-[40px] overflow-hidden">
        <div className="w-full h-full flex flex-col lg:flex-row gap-[24px] justify-between items-stretch min-h-0">
          <ExchangeInfoPanel
            headline={
              <>
                Buy Crypto in less than{" "}
                <span className="text-[#2E9B4F]">5 minutes!</span>
              </>
            }
          />

          <div
            className="w-full max-w-[560px] mx-auto lg:mx-0 flex-1 min-h-0 bg-bg-page-body rounded-[20px] flex flex-col overflow-hidden"
            style={{ boxShadow: "5px 5px 25px 0 rgba(0, 0, 0, 0.10)" }}
          >
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-[24px] py-[24px]">
                <div className="mb-[20px]">
                  <SectionTitle>Payment Information</SectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                    <CurrencyLogoDropdown
                      listOfValues={FIAT_CURRENCIES}
                      value={fiatCurrency}
                      onChange={(value) => setFiatCurrency(value as string)}
                      label="Pay With"
                      placeholder="Select fiat currency"
                      {...dropdownProps}
                    />
                    <GlobalTextInputField
                      title="Payment Method"
                      placeholder="Enter payment method"
                      value={
                        fiatCurrency === "EUR"
                          ? "Open Banking"
                          : fiatCurrency === "CAD"
                            ? "Interac e-Transfer"
                            : "Select fiat currency"
                      }
                      readonly={true}
                      onChange={() => {}}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                  </div>
                </div>

                <div className="mb-[20px]">
                  <SectionTitle>Purchase Details</SectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                    <CurrencyLogoDropdown
                      listOfValues={assetOptions}
                      value={assetId}
                      onChange={setAssetId}
                      label="Receive Asset"
                      placeholder={
                        loadingLists ? "Loading..." : "Select crypto asset"
                      }
                      enableSearch
                      searchableValues={assetOptions.map((item) => [
                        item.currency,
                        item.currencyName || "",
                      ])}
                      {...dropdownProps}
                    />
                    <GlobalTextInputField
                      title="Enter Amount"
                      placeholder="Enter amount"
                      value={amount}
                      onChange={handleAmountChange}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                  </div>
                </div>

                <div className="mb-[20px]">
                  <SectionTitle>Wallet Details</SectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px]">
                    <CurrencyLogoDropdown
                      listOfValues={networkOptions}
                      value={networkId}
                      onChange={setNetworkId}
                      label="Network"
                      placeholder={
                        !assetId
                          ? "Select asset first"
                          : networkOptions.length
                            ? "Select network"
                            : "No networks available"
                      }
                      {...dropdownProps}
                    />
                    <div>
                      <GlobalTextInputField
                        title="Wallet Address"
                        placeholder="Paste wallet address"
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                        inputBorder={
                          walletAddress && !isWalletAddressValid
                            ? "border-border-button-delete rounded-lg"
                            : "border-border-subtle rounded-lg"
                        }
                        suffixIcon={
                          <span className="material-symbols-outlined text-[18px] text-text-subtle">
                            content_paste
                          </span>
                        }
                        suffixIconOnClick={async () => {
                          try {
                            const text = await navigator.clipboard.readText();
                            if (text) setWalletAddress(text.trim());
                          } catch {}
                        }}
                      />
                      {walletAddress && !isWalletAddressValid ? (
                        <p className="mt-[6px] text-[10px] text-text-status-red">
                          Invalid address for{" "}
                          {selectedNetwork?.networkName || "this network"}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div>
                  <SectionTitle>Identity Verification</SectionTitle>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[12px]">
                    <GlobalTextInputField
                      title="First Name"
                      placeholder="Enter first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                    <GlobalTextInputField
                      title="Last Name"
                      placeholder="Enter last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                    <GlobalTextInputField
                      title="Email"
                      type="email"
                      placeholder="Enter email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      inputBorder="border-border-subtle rounded-lg"
                    />
                    <DateOfBirthPicker value={dob} onChange={setDob} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px] mt-[12px]">
                    <CountriesDropdown
                      listOfValues={
                        fiatCurrency === "EUR"
                          ? countries.filter(
                              (country) => country.euEaaCountry === 0,
                            )
                          : countries
                      }
                      value={residenceCountryId}
                      onChange={setResidenceCountryId}
                      label="Country of Residence"
                      placeholder="Select country"
                      enableSearch
                      {...dropdownProps}
                    />
                    <CountriesDropdown
                      listOfValues={countries}
                      value={birthCountryId}
                      onChange={setBirthCountryId}
                      label="Country of Birth"
                      placeholder="Select country"
                      enableSearch
                      {...dropdownProps}
                    />
                  </div>
                  {fiatCurrency === "CAD" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-[8px] mt-[12px]">
                      <GlobalTextInputField
                        title="Mobile"
                        type="tel"
                        placeholder="Enter mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        inputBorder="border-border-subtle rounded-lg"
                      />
                      <CountriesDropdown
                        listOfValues={PROVINCE_OPTIONS}
                        value={province}
                        onChange={setProvince}
                        label="Province"
                        placeholder="Select province"
                        enableSearch
                        {...dropdownProps}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="px-[24px] pb-[24px] pt-[8px] shrink-0">
                {error ? (
                  <div className="mb-[14px] text-[12px] text-text-status-red bg-bg-status-red border border-border-button-delete rounded-lg p-[10px]">
                    {error}
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || loadingLists}
                  className="w-full h-[48px] rounded-full text-white text-[14px] font-semibold disabled:opacity-60"
                  style={{
                    background:
                      "linear-gradient(90deg, #0E18A7 0%, #5E0EAF 51%, #AE0DB2 100%)",
                  }}
                >
                  {submitting ? "Processing..." : "Purchase Now"}
                </button>
                <AccessInfoBadges />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
